import { useLocalObservable, useObserver } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { useMst } from '../models/WithWineState';
import { CartActionOption } from '../types/CartItemProps';
import { CartDispatchData, ProductDispatchData } from '../types/EventDataProps';
import { ProductCardPropsV2 } from '../types/ProductCardProps';
import { TypeProduct } from '../types/TypeProduct';
import {
  createProductData,
  dispatchProductData,
} from '../utils/dispatchHelper';
import { parseBoolean } from '../utils/parseBoolean';
import { useFallbackImage } from '../utils/useFallbackImage';
import IconButton from './IconButton';
import PriceDisplay from './PriceDisplay';
import ProductVariationSelect from './ProductVariationSelect';
import { useToasterContext } from './Toaster';

const UnstyledProductCardV2: React.FC<ProductCardPropsV2> = ({
  className,
  product,
  priceFormat,
  enablevariations,
  format,
  hideBuyControls,
  overrideDecrement,
  overrideIncrement,
  overrideQuantity,
  toasterOptions,
  cartaction = CartActionOption.ShowNotifications,
  variationsort,
  cartV2Enabled = false,
}) => {
  const stateTree = useMst();
  const { showToast } = useToasterContext();
  const [quantity, setQuantity] = useState(0);
  const state = useLocalObservable(() => ({
    get items() {
      return stateTree.session?.items;
    },

    get data() {
      if (product?.id) {
        return stateTree.data.products.get(product.id + '');
      }
      return null;
    },

    get selectedVariation() {
      return !parseBoolean(enablevariations, true)
        ? state.data
        : ((state.data?.selectedVariation ?? state.data) as TypeProduct);
    },

    get cartItem() {
      const item = stateTree.session?.cart.get(
        state.selectedVariation?.id + '',
      );
      return item ?? null;
    },

    get cartQuantity() {
      if (state.cartItem) {
        return state.cartItem.quantity;
      }
      return 0;
    },

    get cartItems(): CartDispatchData[] {
      const cartItems: CartDispatchData[] = [];

      stateTree.session?.cart.forEach((cartItem: any) => {
        const cart = {
          product: createProductData(cartItem.product),
          quantity: cartItem.quantity,
        };
        cartItems.push(cart);
      });

      return cartItems;
    },

    get productEventData(): ProductDispatchData {
      return {
        id: state.selectedVariation?.id,
        skuCode: state.selectedVariation?.skuCode,
        name: state.selectedVariation?.name,
        vintage: state.selectedVariation?.vintage,
        wineType: state.selectedVariation?.wineType,
        productType: state.selectedVariation?.productType,
        productGroup: state.selectedVariation?.productGroup,
      };
    },

    get isLiked() {
      return state.selectedVariation!.liked;
    },

    get isTried() {
      return state.selectedVariation!.tried;
    },

    get isSaleable() {
      return state.selectedVariation?.isSaleableWithStock ?? false;
    },

    get image() {
      return state.selectedVariation?.image ?? state.data?.image;
    },

    get showDisplayQty() {
      if (state.selectedVariation?.websiteProductStock) {
        return (
          state.isSaleable &&
          state.selectedVariation?.websiteProductStock.count <=
            state.selectedVariation.websiteProductStock.displayQuantityLeftAt &&
          state.selectedVariation.websiteProductStock.count > 0 &&
          !!state.selectedVariation.websiteProductStock.quantityLeft
        );
      }
      return state.isSaleable;
    },

    incrementCart() {
      setQuantity(state.cartQuantity + 1);
      if (!state.cartItem) {
        stateTree.session?.addItem(state.selectedVariation!.id);
      } else {
        state.cartItem.increment();
        dispatchProductData(
          'add_to_cart',
          state.productEventData,
          state.cartQuantity,
          state.cartItems,
        );
      }
      this.performCartAction(true);
    },

    decrementCart() {
      setQuantity(state.cartQuantity - 1);
      if (state.cartItem) {
        state.cartItem.decrement();
        dispatchProductData(
          'remove_from_cart',
          state.productEventData,
          state.cartQuantity,
          state.cartItems,
        );
        this.performCartAction(false);
      }

      stateTree.session?.clearZeroQuantityItems();
    },

    removeItem() {
      if (product?.id) {
        stateTree.session?.removeCartItem(product.id);
      }
    },

    performCartAction(isIncrementCart: boolean) {
      if (cartaction === CartActionOption.ShowNotifications) {
        const parent = document.getElementById(
          state.selectedVariation!.id.toString(),
        );
        if (parent) {
          const btn = parent.querySelector('#btn-close') as HTMLElement;
          btn.click();
        }
        const header = isIncrementCart ? 'Added to cart' : 'Removed from cart';
        const message = isIncrementCart
          ? state.selectedVariation?.name
          : state.selectedVariation?.name;
        const subMessage = `${state.cartItem?.quantity} in cart`;
        showToast({
          id: state.selectedVariation!.id,
          link: '#',
          header: header,
          message: message,
          actionLabel: 'View Cart',
          image: state.selectedVariation?.image,
          subMessage: subMessage,
          onClick: stateTree.widgets.cart.showVersion2,
          duration: Number(toasterOptions?.duration),
          toasterAnimation: toasterOptions?.toasterAnimation,
        });
      } else if (cartaction === CartActionOption.ShowCart) {
        stateTree.widgets.cart.showVersion2;
      }
    },

    liked() {
      dispatchProductData(
        'liked',
        state.productEventData,
        state.cartQuantity,
        state.cartItems,
      );
      state.selectedVariation!.onLiked();
    },

    tried() {
      dispatchProductData(
        'tried',
        state.productEventData,
        state.cartQuantity,
        state.cartItems,
      );
      state.selectedVariation!.onTried();
    },
  }));
  useEffect(() => {
    const cartItem = state?.items?.find((x) => x.id.toString() === product?.id);
    if (!cartItem) {
      setQuantity(0);
    } else {
      setQuantity(cartItem.value);
    }
    if (typeof overrideQuantity === 'number') {
      setQuantity(overrideQuantity);
    }
  }, [overrideQuantity, overrideDecrement, state.items]);

  const increment = useCallback(() => {
    if (overrideIncrement) {
      overrideIncrement();
    } else {
      state.incrementCart();
    }
  }, [overrideIncrement, state.incrementCart]);

  const decrement = useCallback(() => {
    if (overrideDecrement) {
      overrideDecrement();
    } else {
      state.decrementCart();
    }
  }, [overrideDecrement, state.decrementCart]);

  const fallbackImage = useFallbackImage(state.image);

  return useObserver(() => {
    return state.selectedVariation ? (
      <div
        className={`ww-product-card ${className ?? ''} ${format ?? ''}`}
        data-product-id={product?.id}
      >
        {!cartV2Enabled ? (
          <div className="ww-product-card-header">
            <IconButton solid={state.isTried} primary onClick={state.tried}>
              <i className="icon-ok" />
            </IconButton>
            <IconButton solid={state.isLiked} primary onClick={state.liked}>
              <i className="icon-heart" />
            </IconButton>
          </div>
        ) : (
          <div className="ww-product-card-header">
            <IconButton
              solid={state.isTried}
              primary
              onClick={state.removeItem}
            >
              <i className="icon-cancel" />
            </IconButton>
          </div>
        )}

        <div className="ww-product-card-content">
          <div className="ww-product-image">
            <a href={state.selectedVariation.link}>
              <img
                src={state.image}
                onError={fallbackImage}
                alt={state.selectedVariation.name}
              />
            </a>
          </div>
          <div className="ww-product-content">
            <small className="ww-product-winery">
              {state.selectedVariation.brandNameOverride ||
                state.selectedVariation.brand?.name}
            </small>
            <h3 className="ww-product-name">
              <a href={state.selectedVariation.link}>
                {state.selectedVariation.name}
              </a>
            </h3>
            <span className="ww-product-subtitle">
              {(state.selectedVariation.region ||
                state.selectedVariation.varietyType) && (
                <small className="ww-product-variety">
                  {state.selectedVariation.varietyType}{' '}
                </small>
              )}
              {state.selectedVariation.vintage &&
                parseInt(state.selectedVariation.vintage) &&
                parseInt(state.selectedVariation.vintage) > 0 && (
                  <small className="ww-product-vintage">
                    {state.selectedVariation.vintage}{' '}
                  </small>
                )}
              {(state.selectedVariation.region ||
                state.selectedVariation.state) && (
                <small className="ww-product-location">
                  {[
                    state.selectedVariation.region,
                    state.selectedVariation.state,
                  ]
                    .filter((i) => !!i)
                    .join(', ')}
                </small>
              )}
            </span>
          </div>
        </div>
        <div className="ww-product-card-footer">
          {state.selectedVariation && (
            <PriceDisplay
              data={state.selectedVariation}
              priceFormat={priceFormat}
              hidePricePrefix
              hidePriceSuffix
            />
          )}
          <span className="divider">/</span>
          {state.data && parseBoolean(enablevariations, true) ? (
            <ProductVariationSelect
              data={state.data}
              variationsort={variationsort}
            />
          ) : !state.data ? (
            ''
          ) : (
            state.data.variationTypeDescription ||
            priceFormat?.suffix ||
            (state.data.minimumUnitPurchase !== null &&
            state.data.minimumUnitPurchase > 1
              ? state.data.isMix
                ? 'Pack'
                : state.data.minimumUnitPurchase.toString()
              : state.data.isWine
              ? 'Bottle'
              : 'Each')
          )}
          <div className="spacer" />
          {!hideBuyControls && (
            <>
              <IconButton solid disabled={quantity === 0} onClick={decrement}>
                <i className="icon-minus" />
              </IconButton>
              <span className="quantity">{quantity}</span>
              <IconButton solid onClick={increment}>
                <i className="icon-plus" />
              </IconButton>
            </>
          )}
        </div>
      </div>
    ) : null;
  });
};

export const ProductCardV2 = styled((props: ProductCardPropsV2) => (
  <>
    <UnstyledProductCardV2 {...props}></UnstyledProductCardV2>
  </>
))`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border-radius: 1.231em;
  box-shadow: 0 0.5em 1em rgba(0, 0, 0, 0.1);
  /* width: 31.923em; */
  min-width: 23.538em;
  color: var(--black);

  &.store {
    flex: none;
    flex-basis: 32%;
  }

  .spacer {
    flex: auto;
  }

  .ww-product-card-header {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0.769em;
    .ww-icon-button {
      flex: none;
      margin-left: 0.769em;
    }
  }

  .ww-product-card-content {
    display: flex;
    padding: 1.538em 0;

    .ww-product-image {
      flex: none;
      width: 6.846em;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      overflow: hidden;
      img {
        height: 6.923em;
        flex: none;
      }
    }

    .ww-product-content {
      min-height: 3em;
      display: flex;
      flex-direction: column;
      padding-right: 1.538em;

      .ww-product-winery {
        margin-right: 6.154em;
        font-size: 1.077em;
      }

      .ww-product-name {
        margin: 0;
        margin-right: 4em;
        font-size: 1.385em;
        font-weight: bold;

        a {
          color: inherit;
          text-decoration: none;
        }
      }

      .ww-product-location,
      .ww-product-vintage,
      .ww-product-variety {
        font-size: 1.077em;
        color: var(--gray-4);
      }

      .ww-product-variety {
        display: none;
      }
    }
  }

  &.store .ww-product-card-content {
    flex: auto;
    flex-direction: column-reverse;
    padding: 1.538em;

    .ww-product-image {
      flex: auto;
      margin-top: 1.538em;
      width: 100%;
      align-items: flex-end;
      justify-content: center;
      img {
        height: 15.385em;
      }
    }
  }

  .ww-product-card-footer {
    border-top: 1px solid var(--gray-2);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.769em;

    .price-info,
    .quantity {
      font-size: 1.385em;
      font-weight: bold;
      font-family: inherit;
      flex: none;
      margin-left: 0.556em;
    }

    .divider {
      font-size: 1.538em;
      color: var(--gray-5);
      margin: 0 0.2em;
      flex: none;
    }

    .variation-select {
      appearance: none;
      padding: 0 0.25em;
      flex: none;
      border: none;
      height: 1.538em;
      display: flex;
      align-items: center;
      font-family: inherit;
      background-color: var(--gray-2);
      border-radius: 0.154em;
      font-size: 1.077em;
    }

    .ww-icon-button {
      flex: none;
      margin-left: 0.769em;
    }
  }
`;

export default ProductCardV2;
